/**
 * Workforce Roles Page
 *
 * Manages workforce role definitions including:
 * - Role CRUD operations
 * - Required skills and certifications
 * - Headcount tracking
 * - Salary ranges
 */

import React, { useState, useEffect } from 'react';
import {
  Briefcase,
  Plus,
  Search,
  Edit,
  Trash2,
  Users,
  Award,
  DollarSign,
  MapPin,
  MoreVertical,
  X,
  Save,
  RefreshCw,
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { PageHeader, PageContainer } from '@/components/weavein';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';

// Helper to get auth token from admin dashboard localStorage
const getAuthToken = (): string | null => {
  return localStorage.getItem('auth_token');
};

interface WorkforceRole {
  id: number;
  role_code: string;
  role_name: string;
  role_type: string;
  description?: string;
  required_skills: string[];
  preferred_skills: string[];
  required_certifications: string[];
  min_experience_years?: number;
  education_requirements?: string;
  salary_range_min?: number;
  salary_range_max?: number;
  target_headcount: number;
  current_headcount: number;
  open_positions: number;
  is_active: boolean;
  created_at: string;
}

interface RoleFormData {
  role_code: string;
  role_name: string;
  role_type: string;
  description: string;
  required_skills: string[];
  preferred_skills: string[];
  required_certifications: string[];
  min_experience_years: number;
  education_requirements: string;
  salary_range_min: number;
  salary_range_max: number;
  target_headcount: number;
}

const ROLE_TYPES = [
  { id: 'chw', label: 'Community Health Worker (CHW)' },
  { id: 'haa', label: 'Health Access Assister (HAA)' },
  { id: 'peer_specialist', label: 'Peer Specialist' },
  { id: 'navigator', label: 'Navigator' },
  { id: 'supervisor', label: 'Supervisor' },
  { id: 'coordinator', label: 'Coordinator' },
  { id: 'other', label: 'Other' },
];

const initialFormData: RoleFormData = {
  role_code: '',
  role_name: '',
  role_type: 'chw',
  description: '',
  required_skills: [],
  preferred_skills: [],
  required_certifications: [],
  min_experience_years: 0,
  education_requirements: '',
  salary_range_min: 0,
  salary_range_max: 0,
  target_headcount: 1,
};

export default function RolesPage() {
  const { t } = useAppTranslation();
  const [roles, setRoles] = useState<WorkforceRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState<WorkforceRole | null>(null);
  const [formData, setFormData] = useState<RoleFormData>(initialFormData);
  const [skillInput, setSkillInput] = useState('');
  const [certInput, setCertInput] = useState('');

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    const token = getAuthToken();
    if (!token) {
      console.warn('No auth token found for roles page');
      setLoading(false);
      return;
    }

    try {
      const response = await authFetch(getWeaveinApiUrl('/api/workforce/roles'));

      if (response.ok) {
        const data = await response.json();
        setRoles(data.items || []);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = getAuthToken();
      const url = editingRole
        ? getWeaveinApiUrl(`/api/workforce/roles/${editingRole.id}`)
        : getWeaveinApiUrl('/api/workforce/roles');

      // Transform frontend data to match backend expected format
      const backendPayload = {
        role_code: formData.role_code,
        role_name: formData.role_name,
        description: formData.description || null,
        // Convert string array to array of objects with skill_name
        required_skills: formData.required_skills.map(skill => ({ skill_name: skill })),
        // For certifications, we store the string names since we don't have certification_type_id mapping
        // The backend will need to handle this or we use a simplified format
        required_certifications: formData.required_certifications.map(cert => ({
          certification_type_id: 0,  // Placeholder - backend may need to look this up
          certification_name: cert,
          is_mandatory: true
        })),
        minimum_education: formData.education_requirements || null,
        minimum_experience_years: formData.min_experience_years,
        target_headcount: formData.target_headcount,
        // Include empty arrays for required fields
        target_sdoh_domains: [],
        community_focus_areas: [],
        language_requirements: [],
      };

      const response = await authFetch(url, {
        method: editingRole ? 'PUT' : 'POST',
        jsonBody: backendPayload,
      });

      if (response.ok) {
        fetchRoles();
        closeModal();
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error saving role:', response.status, errorData);
      }
    } catch (error) {
      console.error('Error saving role:', error);
    }
  };

  const deleteRole = async (roleId: number) => {
    if (!confirm(t('workforce.roles.confirmDelete', 'Are you sure you want to delete this role?'))) return;

    try {
      const token = getAuthToken();
      const response = await authFetch(getWeaveinApiUrl(`/api/workforce/roles/${roleId}`), {
        method: 'DELETE',
      });

      if (response.ok) {
        fetchRoles();
      }
    } catch (error) {
      console.error('Error deleting role:', error);
    }
  };

  const openModal = (role?: WorkforceRole) => {
    if (role) {
      setEditingRole(role);
      // Extract skill/cert names from objects if backend returns objects
      const extractSkillNames = (skills: any[]) =>
        (skills || []).map((s: any) => typeof s === 'string' ? s : s.skill_name).filter(Boolean);
      const extractCertNames = (certs: any[]) =>
        (certs || []).map((c: any) => typeof c === 'string' ? c : c.certification_name).filter(Boolean);

      setFormData({
        role_code: role.role_code,
        role_name: role.role_name,
        role_type: role.role_type,
        description: role.description || '',
        required_skills: extractSkillNames(role.required_skills),
        preferred_skills: extractSkillNames(role.preferred_skills),
        required_certifications: extractCertNames(role.required_certifications),
        min_experience_years: role.min_experience_years || 0,
        education_requirements: role.education_requirements || '',
        salary_range_min: role.salary_range_min || 0,
        salary_range_max: role.salary_range_max || 0,
        target_headcount: role.target_headcount,
      });
    } else {
      setEditingRole(null);
      setFormData(initialFormData);
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingRole(null);
    setFormData(initialFormData);
    setSkillInput('');
    setCertInput('');
  };

  const addSkill = (type: 'required' | 'preferred') => {
    if (!skillInput.trim()) return;
    const field = type === 'required' ? 'required_skills' : 'preferred_skills';
    if (!formData[field].includes(skillInput.trim())) {
      setFormData({ ...formData, [field]: [...formData[field], skillInput.trim()] });
    }
    setSkillInput('');
  };

  const removeSkill = (skill: string, type: 'required' | 'preferred') => {
    const field = type === 'required' ? 'required_skills' : 'preferred_skills';
    setFormData({ ...formData, [field]: formData[field].filter(s => s !== skill) });
  };

  const addCertification = () => {
    if (!certInput.trim()) return;
    if (!formData.required_certifications.includes(certInput.trim())) {
      setFormData({
        ...formData,
        required_certifications: [...formData.required_certifications, certInput.trim()],
      });
    }
    setCertInput('');
  };

  const filteredRoles = roles.filter(role =>
    role.role_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    role.role_code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatSalary = (min?: number, max?: number) => {
    if (!min && !max) return '-';
    if (min && max) return `$${min.toLocaleString()} - $${max.toLocaleString()}`;
    if (min) return `$${min.toLocaleString()}+`;
    return `Up to $${max?.toLocaleString()}`;
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Workforce · Roles"
        title={t('workforce.roles.title', 'Workforce Roles')}
        description={t('workforce.roles.description', 'Define and manage workforce role definitions')}
        actions={
          <button
            onClick={() => openModal()}
            className="inline-flex items-center gap-1.5 rounded-md bg-v2accent px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
          >
            <Plus className="w-4 h-4" />
            {t('workforce.roles.createRole', 'Create Role')}
          </button>
        }
      />
      <PageContainer>
      <div className="space-y-6">
        {/* Search */}
        <div className="bg-surface rounded-xl border border-v2border p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('workforce.roles.searchPlaceholder', 'Search roles...')}
                className="w-full pl-10 pr-4 py-2 border border-v2border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <button
              onClick={fetchRoles}
              className="p-2 border border-v2border rounded-lg hover:bg-paper"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Roles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <div className="col-span-full text-center py-12 text-ink-500">{t('workforce.roles.loading', 'Loading...')}</div>
          ) : filteredRoles.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <Briefcase className="w-12 h-12 text-ink-400 mx-auto mb-4" />
              <p className="text-ink-500">{t('workforce.roles.noRoles', 'No roles defined yet')}</p>
              <button
                onClick={() => openModal()}
                className="mt-4 text-emerald-600 hover:text-emerald-700 font-medium"
              >
                {t('workforce.roles.createFirstRole', 'Create your first role')}
              </button>
            </div>
          ) : (
            filteredRoles.map((role) => (
              <div
                key={role.id}
                className="bg-surface rounded-xl border border-v2border p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <Briefcase className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-ink">{role.role_name}</h3>
                      <p className="text-sm text-ink-500">{role.role_code}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => openModal(role)}
                      className="p-2 hover:bg-paper-2 rounded"
                      title={t('workforce.roles.edit', 'Edit')}
                    >
                      <Edit className="w-4 h-4 text-ink-500" />
                    </button>
                    <button
                      onClick={() => deleteRole(role.id)}
                      className="p-2 hover:bg-paper-2 rounded"
                      title={t('workforce.roles.delete', 'Delete')}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>

                {role.description && (
                  <p className="mt-3 text-sm text-ink-500 line-clamp-2">{role.description}</p>
                )}

                {/* Stats */}
                <div className="mt-4 grid grid-cols-3 gap-4 pt-4 border-t border-v2border-muted">
                  <div>
                    <div className="flex items-center text-ink-400 text-xs mb-1">
                      <Users className="w-3 h-3 mr-1" />
                      {t('workforce.roles.headcount', 'Headcount')}
                    </div>
                    <p className="text-lg font-semibold text-ink">
                      {role.current_headcount}/{role.target_headcount}
                    </p>
                  </div>
                  <div>
                    <div className="flex items-center text-ink-400 text-xs mb-1">
                      <Briefcase className="w-3 h-3 mr-1" />
                      {t('workforce.roles.open', 'Open')}
                    </div>
                    <p className="text-lg font-semibold text-emerald-600">{role.open_positions}</p>
                  </div>
                  <div>
                    <div className="flex items-center text-ink-400 text-xs mb-1">
                      <Award className="w-3 h-3 mr-1" />
                      {t('workforce.roles.certs', 'Certs')}
                    </div>
                    <p className="text-lg font-semibold text-ink">
                      {role.required_certifications?.length || 0}
                    </p>
                  </div>
                </div>

                {/* Skills */}
                {role.required_skills?.length > 0 && (
                  <div className="mt-4">
                    <div className="flex flex-wrap gap-1">
                      {role.required_skills.slice(0, 3).map((skill: any, i) => (
                        <span
                          key={i}
                          className="px-2 py-0.5 bg-paper-2 text-ink-500 rounded text-xs"
                        >
                          {typeof skill === 'string' ? skill : skill.skill_name}
                        </span>
                      ))}
                      {role.required_skills.length > 3 && (
                        <span className="px-2 py-0.5 text-ink-400 text-xs">
                          +{role.required_skills.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Salary */}
                {(role.salary_range_min || role.salary_range_max) && (
                  <div className="mt-3 flex items-center text-sm text-ink-500">
                    <DollarSign className="w-4 h-4 mr-1" />
                    {formatSalary(role.salary_range_min, role.salary_range_max)}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4">
              <div className="fixed inset-0 bg-black/50" onClick={closeModal} />
              <div className="relative bg-surface rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-surface px-6 py-4 border-b border-v2border flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-ink">
                    {editingRole ? t('workforce.roles.editRole', 'Edit Role') : t('workforce.roles.createNewRole', 'Create New Role')}
                  </h2>
                  <button onClick={closeModal} className="p-2 hover:bg-paper-2 rounded">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-ink-700 mb-1">{t('workforce.roles.form.roleCode', 'Role Code')}</label>
                      <input
                        type="text"
                        value={formData.role_code}
                        onChange={(e) => setFormData({ ...formData, role_code: e.target.value.toUpperCase() })}
                        placeholder={t('workforce.roles.form.roleCodePlaceholder', 'e.g., CHW-001')}
                        className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-ink-700 mb-1">{t('workforce.roles.form.roleType', 'Role Type')}</label>
                      <select
                        value={formData.role_type}
                        onChange={(e) => setFormData({ ...formData, role_type: e.target.value })}
                        className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      >
                        {ROLE_TYPES.map(type => (
                          <option key={type.id} value={type.id}>{type.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">{t('workforce.roles.form.roleName', 'Role Name')}</label>
                    <input
                      type="text"
                      value={formData.role_name}
                      onChange={(e) => setFormData({ ...formData, role_name: e.target.value })}
                      placeholder={t('workforce.roles.form.roleNamePlaceholder', 'e.g., Community Health Worker - Diabetes')}
                      className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">{t('workforce.roles.form.description', 'Description')}</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>

                  {/* Requirements */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-ink-700 mb-1">{t('workforce.roles.form.minExperience', 'Min Experience (years)')}</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.min_experience_years}
                        onChange={(e) => setFormData({ ...formData, min_experience_years: parseInt(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-ink-700 mb-1">{t('workforce.roles.form.targetHeadcount', 'Target Headcount')}</label>
                      <input
                        type="number"
                        min="1"
                        value={formData.target_headcount}
                        onChange={(e) => setFormData({ ...formData, target_headcount: parseInt(e.target.value) || 1 })}
                        className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        required
                      />
                    </div>
                  </div>

                  {/* Salary Range */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-ink-700 mb-1">{t('workforce.roles.form.salaryMin', 'Salary Min ($)')}</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.salary_range_min || ''}
                        onChange={(e) => setFormData({ ...formData, salary_range_min: parseInt(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-ink-700 mb-1">{t('workforce.roles.form.salaryMax', 'Salary Max ($)')}</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.salary_range_max || ''}
                        onChange={(e) => setFormData({ ...formData, salary_range_max: parseInt(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>

                  {/* Skills */}
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">{t('workforce.roles.form.requiredSkills', 'Required Skills')}</label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={skillInput}
                        onChange={(e) => setSkillInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill('required'))}
                        placeholder={t('workforce.roles.form.addSkillPlaceholder', 'Add a skill...')}
                        className="flex-1 px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                      <button
                        type="button"
                        onClick={() => addSkill('required')}
                        className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200"
                      >
                        {t('workforce.roles.form.add', 'Add')}
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.required_skills.map((skill, i) => (
                        <span
                          key={i}
                          className="px-2 py-1 bg-paper-2 text-ink-700 rounded flex items-center text-sm"
                        >
                          {skill}
                          <button
                            type="button"
                            onClick={() => removeSkill(skill, 'required')}
                            className="ml-1 text-ink-400 hover:text-ink-500"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end space-x-3 pt-4 border-t border-v2border">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 border border-v2border rounded-lg hover:bg-paper"
                    >
                      {t('workforce.roles.form.cancel', 'Cancel')}
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {editingRole ? t('workforce.roles.form.updateRole', 'Update Role') : t('workforce.roles.form.createRole', 'Create Role')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
      </PageContainer>
    </div>
  );
}
